/* "Renew document" modal: new expiry + optional scan upload (S3 folder "documents"), with the compact renewal guide. */
import { useId, useState } from "react";
import { useRenewDocument, useUploadFile } from "../../../data/hooks";
import { addDays } from "../../../domain/dates";
import { useI18n } from "../../../i18n";
import { Alert, Button, DualDate, Field, Input, Kv, Modal, errMsg, useToast } from "../../../ui";
import RenewalGuide from "./RenewalGuide";
import { fieldErrors } from "./shared";

export interface RenewTarget { employeeId: number; employeeName: string; kind: string; expiry: string }

export default function RenewModal({ target, today, onClose, onDone }: { target: RenewTarget; today: string; onClose: () => void; onDone?: () => void }) {
  const { L, t } = useI18n();
  const toast = useToast();
  const renew = useRenewDocument();
  const upload = useUploadFile();
  const base = target.expiry > today ? target.expiry : today;
  const [expiry, setExpiry] = useState(addDays(base, 365));
  const [file, setFile] = useState<File | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const dateId = useId();
  const fileId = useId();
  const busy = renew.isPending || upload.isPending;

  const submit = async () => {
    setErrs({});
    if (!expiry || expiry <= today) { setErrs({ expiry: L("Pick a date after today", "اختر تاريخًا بعد اليوم") }); return; }
    try {
      let fileKey: string | undefined;
      if (file) fileKey = (await upload.mutateAsync({ file, name: file.name, folder: "documents" })).key;
      await renew.mutateAsync({ employeeId: target.employeeId, kind: target.kind, expiry, fileKey });
      toast(L(target.kind + " renewed until " + expiry, "تم تجديد " + t(target.kind) + " حتى " + expiry));
      onDone?.();
      onClose();
    } catch (e) {
      const f = fieldErrors(e);
      if (Object.keys(f).length) setErrs(f);
      else toast(errMsg(e), "bad");
    }
  };

  return (
    <Modal
      title={t("Renew") + " · " + t(target.kind)}
      onClose={onClose}
      testId="renew-modal"
      className="wide"
      foot={<><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" icon="check" loading={busy} onClick={submit} data-testid="renew-submit">{L("Save renewal", "حفظ التجديد")}</Button></>}
    >
      <div className="stack">
        <Kv items={[[t("Employee"), target.employeeName], [t("Document"), t(target.kind)], [L("Current expiry", "الانتهاء الحالي"), <DualDate date={target.expiry} inline />]]} />
        <div className="form2">
          <Field label={L("New expiry date", "تاريخ الانتهاء الجديد")} req htmlFor={dateId} err={errs.expiry} hint={<DualDate date={expiry} inline />}>
            <Input id={dateId} type="date" value={expiry} min={addDays(today, 1)} onChange={(e) => setExpiry(e.target.value)} bad={!!errs.expiry} data-testid="renew-expiry" />
          </Field>
          <Field label={L("Renewed copy (optional)", "النسخة المجددة (اختياري)")} htmlFor={fileId} err={errs.fileKey || errs.file} hint={L("PDF, JPG or PNG · up to 10 MB", "PDF أو JPG أو PNG · حتى 10 ميغابايت")}>
            <Input id={fileId} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="renew-file" />
          </Field>
        </div>
        {target.kind === "Iqama" && <Alert tone="info" tint title={L("The work permit follows the Iqama", "رخصة العمل تتبع الإقامة")} desc={L("Saving also moves the work permit expiry to the same date.", "سيُحدَّث تاريخ انتهاء رخصة العمل إلى التاريخ نفسه.")} />}
        <RenewalGuide kind={target.kind} compact audience="employer" />
      </div>
    </Modal>
  );
}
